const VALID_BLOCK_TYPES = new Set(['text', 'imageText', 'image', 'gallery']);
const VALID_PLACEMENTS = new Set(['text-left', 'image-left', 'stacked']);
const VALID_IMAGE_TEXT_SEQUENCES = new Set(['before-details', 'after-details', 'after-images']);
const toSafeSequence = (value) => (VALID_IMAGE_TEXT_SEQUENCES.has(value) ? value : null);

const toSafeIndex = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && value.trim() === '') return null;

  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 0) return null;
  return parsed;
};

const getBlockId = () => `block-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const normalizeImageIndexes = (indexes) => {
  if (!Array.isArray(indexes)) return [];

  return indexes
    .map((index) => toSafeIndex(index))
    .filter((index) => index !== null);
};

export const createContentBlock = (type = 'text', imageCount = 0) => {
  const blockType = VALID_BLOCK_TYPES.has(type) ? type : 'text';
  const defaultImageIndex = imageCount > 0 ? 0 : null;

  if (blockType === 'text') {
    return {
      id: getBlockId(),
      type: 'text',
      text: '',
      sequence: 'after-details'
    };
  }

  if (blockType === 'imageText') {
    return {
      id: getBlockId(),
      type: 'imageText',
      text: '',
      placement: 'text-left',
      sequence: 'after-details',
      imageIndex: defaultImageIndex
    };
  }

  if (blockType === 'image') {
    return {
      id: getBlockId(),
      type: 'image',
      sequence: 'after-details',
      imageIndex: defaultImageIndex
    };
  }

  return {
    id: getBlockId(),
    type: 'gallery',
    sequence: 'after-details',
    useAllImages: true,
    imageIndexes: []
  };
};

export const sanitizeContentBlocks = (blocks) => {
  if (!Array.isArray(blocks)) return [];

  return blocks.reduce((result, block, index) => {
    if (!block || !VALID_BLOCK_TYPES.has(block.type)) {
      return result;
    }

    const id = typeof block.id === 'string' && block.id.trim() ? block.id : `block-${index}`;

    if (block.type === 'text') {
      result.push({
        id,
        type: 'text',
        text: typeof block.text === 'string' ? block.text : '',
        sequence: toSafeSequence(block.sequence)
      });
      return result;
    }

    if (block.type === 'imageText') {
      const placement = VALID_PLACEMENTS.has(block.placement) ? block.placement : 'text-left';
      const sequence = toSafeSequence(block.sequence);
      result.push({
        id,
        type: 'imageText',
        text: typeof block.text === 'string' ? block.text : '',
        placement,
        sequence,
        imageIndex: toSafeIndex(block.imageIndex)
      });
      return result;
    }

    if (block.type === 'image') {
      result.push({
        id,
        type: 'image',
        sequence: toSafeSequence(block.sequence),
        imageIndex: toSafeIndex(block.imageIndex)
      });
      return result;
    }

    result.push({
      id,
      type: 'gallery',
      sequence: toSafeSequence(block.sequence),
      useAllImages: block.useAllImages !== false,
      imageIndexes: normalizeImageIndexes(block.imageIndexes)
    });

    return result;
  }, []);
};

export const resolveContentBlocks = (blocks, images = []) => {
  const safeImages = Array.isArray(images) ? images : [];
  const safeBlocks = sanitizeContentBlocks(blocks);

  return safeBlocks.map((block) => {
    if (block.type === 'imageText') {
      return {
        ...block,
        imageSrc: block.imageIndex !== null ? safeImages[block.imageIndex] || null : null
      };
    }

    if (block.type === 'image') {
      return {
        ...block,
        imageSrc: block.imageIndex !== null ? safeImages[block.imageIndex] || null : null
      };
    }

    if (block.type === 'gallery') {
      const indexes = block.useAllImages
        ? safeImages.map((_, index) => index)
        : normalizeImageIndexes(block.imageIndexes);

      return {
        ...block,
        galleryImages: indexes
          .map((index) => ({ index, src: safeImages[index] || null }))
          .filter((item) => Boolean(item.src))
      };
    }

    return block;
  });
};

const swapIndex = (value, first, second) => {
  if (value === first) return second;
  if (value === second) return first;
  return value;
};

export const remapContentBlocksAfterImageSwap = (blocks, firstIndex, secondIndex) => {
  return sanitizeContentBlocks(blocks).map((block) => {
    if (block.type === 'imageText' || block.type === 'image') {
      if (block.imageIndex === null) return block;
      return {
        ...block,
        imageIndex: swapIndex(block.imageIndex, firstIndex, secondIndex)
      };
    }

    if (block.type === 'gallery' && !block.useAllImages) {
      return {
        ...block,
        imageIndexes: block.imageIndexes.map((index) => swapIndex(index, firstIndex, secondIndex))
      };
    }

    return block;
  });
};

export const remapContentBlocksAfterImageRemove = (blocks, removedIndex) => {
  return sanitizeContentBlocks(blocks).map((block) => {
    if (block.type === 'imageText' || block.type === 'image') {
      if (block.imageIndex === null) return block;
      if (block.imageIndex === removedIndex) {
        return {
          ...block,
          imageIndex: null
        };
      }

      if (block.imageIndex > removedIndex) {
        return {
          ...block,
          imageIndex: block.imageIndex - 1
        };
      }

      return block;
    }

    if (block.type === 'gallery' && !block.useAllImages) {
      const nextIndexes = block.imageIndexes
        .filter((index) => index !== removedIndex)
        .map((index) => (index > removedIndex ? index - 1 : index));

      return {
        ...block,
        imageIndexes: nextIndexes
      };
    }

    return block;
  });
};

export const parseImageIndexesInput = (value) => {
  if (typeof value !== 'string') return [];

  return value
    .split(',')
    .map((part) => toSafeIndex(part.trim()))
    .filter((index) => index !== null);
};
